import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Input, message, Modal, Select, Spin, Tooltip, Upload } from 'antd';
import {
    useGetAllUserGuideGroupsQuery,
    useGetAllUserGuidesQuery,
    useGetUserGuideUploadLanguagesQuery,
} from './queries.generated';
import { GridApi, ICellRendererParams, ColDef } from 'ag-grid-community';
import {
    useCreateUserGuideMutation,
    useDeleteUserGuideMutation,
    useUpdateUserGuideMutation,
    useUploadUserGuideDocumentMutation,
} from './mutations.generated';
import { useAppDispatch, useAppSelector } from '../../main/hooks';
import { AgGridReact } from 'ag-grid-react';
import { invalidateUserGuideCache } from './HelpUserGuideService';
import { triggerLanguageChange } from '../../store/IEDPGlobalSlice';
import { getI18n } from '../../utils/Utils';
import { DeleteOutlined, EditOutlined, PlusSquareOutlined, UploadOutlined } from '@ant-design/icons';
import { IAppComponentProps } from '../../components';

const { Option } = Select;

const UserGuideContentManagement = ({ intl, currentUser }: IAppComponentProps) => {
    const [form] = Form.useForm();
    const { data, loading, error, refetch } = useGetAllUserGuidesQuery();
    const userGuideLanguageData = useGetUserGuideUploadLanguagesQuery();
    const { data: groupsData } = useGetAllUserGuideGroupsQuery();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [editingGuide, setEditingGuide] = useState<any>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [manualLoading, setManualLoading] = useState<boolean>(false);
    const [gridApi, setGridApi] = useState<GridApi>(undefined);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [updateUserGuide] = useUpdateUserGuideMutation();
    const [createUserGuide] = useCreateUserGuideMutation();
    const [deleteUserGuide] = useDeleteUserGuideMutation();
    const [uploadUserGuideDocument] = useUploadUserGuideDocumentMutation();
    const [_flag,setFlag] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { rerenderAfterLanguageChange } = useAppSelector((state) => state.globalStateReducer);

    const gridRef = useRef<AgGridReact>(null);

    const refreshScreen = ()=>setFlag(val=>!val);

    const languageMap = useRef<{[key:number]:string}>({});

    const showModal = () => {
        setIsModalVisible(true);
        setIsEditing(false);
        form.resetFields();
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingGuide(null);
        form.resetFields();
    };

    useEffect(() => {
        if (data) {
            refetch().finally(() => setManualLoading(false));
        }
    }, [rerenderAfterLanguageChange]);

    useEffect(() => {
        if (groupsData?.getAllUserGuideGroups.length) {
            setSelectedGroupId(groupsData.getAllUserGuideGroups[0].userGuideGroupId);
        }
    }, [groupsData]);

    const fetchUserGuideData = () => {
        if (data?.getAllUserGuides && groupsData?.getAllUserGuideGroups.length && selectedGroupId) {
            return data?.getAllUserGuides.filter((item) => item.userGuideGroup.userGuideGroupId === selectedGroupId);
        }

        return [];
    };

    const handleGroupChange = (value: number | null) => {
        setSelectedGroupId(value);
        languageMap.current = {};
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setManualLoading(true);

            if (isEditing && editingGuide) {
                await updateUserGuide({
                    variables: {
                        input: {
                            userGuideId: editingGuide.userGuideId,
                            userGuideGroupId: selectedGroupId,
                            sortOrder: editingGuide.sortOrder,
                            translationRequest: {
                                phraseID: editingGuide.userGuideNamePhraseId,
                                translations: [
                                    { language: 'en', content: values.englishTranslation },
                                    { language: 'de', content: values.germanTranslation },
                                ],
                            },
                        },
                    },
                });
                message.success(intl.formatMessage({ id: 'Download Success' }));
            } else {
                const generatePhraseID = (englishText: string): string => {
                    const prefix = 'user-guide-';
                    const sanitizedText = englishText
                        .toLowerCase()
                        .replace(/\s+/g, '-')
                        .replace(/[^a-z\-]/g, '')
                        .replace(/--+/g, '-')
                        .trim()
                        .replace(/^-|-$/g, '');

                    const randomNumber = Math.floor(Math.random() * 9) + 1;

                    return `${prefix}${sanitizedText}-${randomNumber}`;
                };

                await createUserGuide({
                    variables: {
                        input: {
                            userGuideGroupId: selectedGroupId,
                            sortOrder: fetchUserGuideData().length + 1,
                            translationRequest: {
                                phraseID: generatePhraseID(values.englishTranslation),
                                translations: [
                                    { language: 'en', content: values.englishTranslation },
                                    { language: 'de', content: values.germanTranslation },
                                ],
                            },
                        },
                    },
                });
                message.success(intl.formatMessage({ id: 'Download Success' }));
            }

            setIsModalVisible(false);
            invalidateUserGuideCache();
            dispatch(triggerLanguageChange());
        } catch (error) {
            console.error('Error creating/updating user guide:', error);
            message.error(intl.formatMessage({ id: 'Download Error' }));
            setManualLoading(false);
        }
    };

    const handleEdit = (guide: any) => {
        setIsEditing(true);
        setEditingGuide(guide);
        setIsModalVisible(true);
        form.setFieldsValue({
            englishTranslation: guide.translations.find((t) => t.languageCode.toLowerCase().trim() === 'en')
                ?.translation,
            germanTranslation: guide.translations.find((t) => t.languageCode.toLowerCase().trim() === 'de')
                ?.translation,
        });
    };

    const handleDelete = async (guide: any) => {
        try {
            setManualLoading(true);
            await deleteUserGuide({
                variables: { id: guide.userGuideId },
            });
            message.success(intl.formatMessage({ id: 'Download Success' }));
            refetch().then(_=> invalidateUserGuideCache());
        } catch (error) {
            console.error('Error deleting user guide:', error);
            message.error(intl.formatMessage({ id: 'Download Error' }));
        } finally {
            setManualLoading(false);
        }
    };

    const convertToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (e) =>  {
                const firstIndex = (e.target.result && ((String)(e.target.result)).indexOf('base64,')) || -1;
                const content = firstIndex>0? (String)(e.target.result).substring(firstIndex+7): (String)(e.target.result);
                resolve(content);
            };

            reader.onerror = (error) => reject(error);
        });
    };

    const handleUpload = async (guide: any, file: File, language: string) => {
        try {
            setManualLoading(true);
            const base64 = await convertToBase64(file);
            await uploadUserGuideDocument({
                variables: {
                    input: {
                        userGuideId: guide.userGuideId,
                        language,
                        blobFileEncoded: {
                            fileName: file.name,
                            mimeType: file.type,
                            base64FileData: base64,
                            requiresAuthentication:false
                        }
                    },
                },
            });
            message.success(intl.formatMessage({ id: 'Download Success' }));
            refetch().finally(() => invalidateUserGuideCache());
        } catch (error) {
            console.error('Error uploading user guide document:', error);
            message.error(intl.formatMessage({ id: 'Download Error' }));
        } finally {
            setManualLoading(false);
        }
    };


    const beforeUpload = (file: File) => {
        const isPDF = file.type === 'application/pdf';
        if (!isPDF) {
            message.error('You can only upload PDF files!');
        }
        return isPDF;
    };

    const ActionsCellRenderer =  (props: ICellRendererParams) => {
            const { data } = props;

            const [uploadLanguage, setUploadLanguage] = useState<string>(languageMap.current[data.userGuideId] || 'EN');

            return (
                <div>
                    <Tooltip title={getI18n('draft-document-row-options-edit', 'Edit', intl)} key={'editUserGuide'}>
                        <EditOutlined
                            style={{ color: '#444444', marginRight: '5px' }}
                            onClick={(_) => handleEdit(data)}
                        />
                    </Tooltip>
                    <Tooltip title={getI18n('delete', 'Delete', intl)} key={'removeUserGuide'}>
                        <DeleteOutlined
                            style={{ color: '#444444', marginRight: '5px' }}
                            onClick={(_) => handleDelete(data)}
                        />
                    </Tooltip>
                    <Upload
                        accept=".pdf"
                        beforeUpload={(file) => {
                            if (beforeUpload(file)) {
                                handleUpload(data, file, uploadLanguage);
                            }
                            return false;
                        }}
                        showUploadList={false}
                    >
                        <Tooltip
                            title={
                            intl.formatMessage(
                                {id:data.userGuideBlobs.filter(item=>item.language.toUpperCase()===uploadLanguage).length?'replace':'ecap-upload-doc'
                                })}
                            key={'uploadUserGuide'}>
                            <Button icon={<UploadOutlined />} size="small" />
                        </Tooltip>
                    </Upload>
                    {gridApi && userGuideLanguageData?.data &&
                        <Tooltip title={intl.formatMessage({id:'upload-document-language'})}>
                                <Select
                                    style={{margin: "0px 5px", height:'20px'}}
                                    defaultValue={uploadLanguage}
                                    onChange={theLanguage=> {
                                        setUploadLanguage(theLanguage);
                                        languageMap.current[data.userGuideId] = theLanguage;
                                        refreshScreen();
                                    }}
                                >
                                {
                                    userGuideLanguageData.data.getUserGuideUploadLanguages.languages.map(language => <Option key={`upload-language-${language}`} value={language.toUpperCase()}>
                                        {language.toUpperCase()}
                                    </Option>)
                                }
                            </Select>
                        </Tooltip>
                    }
                </div>
            );
        };

    const GUIDE_COLUMN_DEFINITIONS: ColDef[] = [
        {
            headerName: intl.formatMessage({
                id: 'document-draft-grid-header-Options',
                defaultMessage: 'Options',
            }),
            field: 'draftDocumentRowOptions',
            colId: 'draftDocumentRowOptions',
            cellRenderer: ActionsCellRenderer,
            width: 230,
            rowDrag: true,
        },
        {
            field: 'sortOrder',
            headerName: intl.formatMessage({ id: 'sort-order' }),
        },
        {
            field: 'userGuideNamePhraseId',
            colId: 'userGuideNamePhraseId',
            headerName: intl.formatMessage({ id: 'phrase-description' }),
            tooltipValueGetter:(params) => {
                return intl.formatMessage({ id: params.data.userGuideNamePhraseId.trim() });
            },
            valueGetter: (params) => {
                return intl.formatMessage({ id: params.data.userGuideNamePhraseId.trim() });
            },
        },
        {
            field: 'userGuideFileName',
            headerName: intl.formatMessage({ id: 'freight-documents-file-name' }),
            tooltipValueGetter:(params) => {
                const selectedLanguage = languageMap.current[params.data.userGuideId] || 'EN';
                return params.data.userGuideBlobs.find(    item => item.language.toUpperCase() === selectedLanguage)?.blob?.fileName || '';
            },
            valueGetter: (params) => {
                const selectedLanguage = languageMap.current[params.data.userGuideId] || 'EN';
                return params.data.userGuideBlobs.find(    item => item.language.toUpperCase() === selectedLanguage)?.blob?.fileName || '';
            },
        },
        {
            field: 'insertUser',
            headerName: intl.formatMessage({ id: 'freight-documents-insert-user' }),
        },
        {
            field: 'insertDate',
            headerName: intl.formatMessage({ id: 'freight-documents-insert-time' }),
        },
        {
            field: 'updateUser',
            headerName: intl.formatMessage({ id: 'freight-documents-update-user' }),
        },
        {
            field: 'updateDate',
            headerName: intl.formatMessage({ id: 'freight-documents-update-time' }),
        },
    ];

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    const doUserGuideUpdates = async (userGuideSortUpdates: any[]) => {
        setManualLoading(true);
        try {
            for (const update of userGuideSortUpdates) {
                await updateUserGuide({
                    variables: {
                        input: {
                            userGuideId: update.userGuideId,
                            userGuideGroupId: update.userGuideGroup.userGuideGroupId,
                            sortOrder: update.sortOrder,
                            translationRequest: {
                                phraseID: update.userGuideNamePhraseId,
                                translations: update.translations.map((item) => {
                                    return {
                                        language: item.languageCode,
                                        content: item.translation,
                                    };
                                }),
                            },
                        },
                    },
                });
            }
            message.success(intl.formatMessage({ id: 'Download Success' }));
            return true;
        } catch (error) {
            console.error('Error updating user guides:', error);
            message.error(intl.formatMessage({ id: 'Download Error' }));
            return false;
        } finally {
            setManualLoading(false);
        }
    };

    const onRowDragEnd = (_event: any) => {
        const userGuideSortUpdates: any[] = [];

        const rowCount = gridApi.getModel().getRowCount();

        for (let i = 0; i < rowCount; i++) {
            const theNode = gridApi.getModel().getRow(i);
            if (theNode.rowIndex + 1 !== theNode.data.sortOrder) {
                userGuideSortUpdates.push({
                    ...theNode.data,
                    sortOrder: i + 1,
                });
            }
        }

        if (userGuideSortUpdates.length > 0) {
            doUserGuideUpdates(userGuideSortUpdates).then((result) => {
                if (result) {
                    refetch().finally(() => invalidateUserGuideCache());
                }
            });
        }
    };

    return (
        <div>
            <div className="pl15 ag-theme-balham">
                <div style={{ margin: '10px 0px' }}>
                    {intl.formatMessage({ id: 'reorder-rows-to-change-sort-order' })}
                </div>
                <Spin spinning={loading || manualLoading}>
                    <Tooltip title={getI18n('add', 'Add', intl)} key={'addUserGuide'}>
                        <PlusSquareOutlined
                            style={{
                                fontSize: '25px',
                                color: '#444444',
                                marginRight: '15px',
                            }}
                            onClick={(_) => showModal()}
                        />
                    </Tooltip>

                    {groupsData?.getAllUserGuideGroups.length && (
                        <div style={{ margin: '10px 0px' }}>
                            <Select
                                style={{ width: 200 }}
                                defaultValue={groupsData.getAllUserGuideGroups[0].userGuideGroupId}
                                placeholder={intl.formatMessage({ id: 'e-cap-capture-currency' })}
                                onChange={handleGroupChange}
                            >
                                {groupsData?.getAllUserGuideGroups.map((group) => (
                                    <Option key={group.userGuideGroupId} value={group.userGuideGroupId}>
                                        {intl.formatMessage({ id: group.userGuideGroupNamePhraseId })}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    <div style={{ flex: 'auto', height: '500px', paddingTop: '10px' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={fetchUserGuideData()}
                            columnDefs={GUIDE_COLUMN_DEFINITIONS}
                            defaultColDef={{
                                sortable: false,
                                filter: false,
                                resizable: true,
                            }}
                            onGridReady={onGridReady}
                            sideBar={false}
                            debug={false}
                            rowDragManaged={true}
                            animateRows={true}
                            onRowDragEnd={onRowDragEnd}
                        />
                    </div>
                </Spin>
            </div>
            <Modal
                title={intl
                    .formatMessage({ id: 'add-or-edit-user-guide' })
                    .replace(
                        '{0}',
                        isEditing
                            ? intl.formatMessage({ id: 'draft-document-row-options-edit' })
                            : intl.formatMessage({ id: 'add' })
                    )}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                confirmLoading={manualLoading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="englishTranslation"
                        label={intl.formatMessage({ id: 'english-translation' })}
                        rules={[
                            { required: true, message: intl.formatMessage({ id: 'generic-requiredField-message' }) },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="germanTranslation"
                        label={intl.formatMessage({ id: 'german-translation' })}
                        rules={[
                            { required: true, message: intl.formatMessage({ id: 'generic-requiredField-message' }) },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserGuideContentManagement;
